import { useMemo } from 'react';
import {
  ContactInteractionChannelSlug,
  ConversationWhereInput,
  QueryConversationsArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { CONVERSATIONS_QUERY_PAGE_SIZE } from 'src/components/pages/conversations/components/conversations/conversation-section/conversations-section';
import {
  ConversationAssigneeType,
  ConversationsFilters,
} from 'src/services/conversation.service';

export const useConversationQueryVariables = (options: {
  filters: ConversationsFilters;
  institutionId: number;
  userId: number;
}): Partial<QueryConversationsArgs> => {
  const { filters, institutionId, userId } = options;

  return useMemo(() => {
    const where: ConversationWhereInput[] = [{ lastContactInteraction: { _ne: null } }];

    if (filters.keyword) {
      where.push({
        matchKeywords: {
          _all: filters.keyword,
        },
      });
    }

    if (filters.contactTagsIds.length) {
      where.push({
        contact: {
          tags: {
            id: { _in: filters.contactTagsIds },
          },
        },
      });
    }

    if (filters.contactStatusesIds.length) {
      where.push({
        contact: {
          status: {
            id: { _in: filters.contactStatusesIds },
          },
        },
      });
    } else if (filters.contactStagesIds.length) {
      where.push({
        contact: {
          stage: {
            id: { _in: filters.contactStagesIds },
          },
        },
      });
    }

    switch (filters.assigneeType) {
      case ConversationAssigneeType.ONLY_ME:
        where.push({
          contact: {
            assignee: {
              id: { _in: [userId] },
            },
          },
        });
        break;
      case ConversationAssigneeType.UNASSIGNED:
        where.push({
          contact: {
            assignee: { _eq: null },
          },
        });
        break;
      case ConversationAssigneeType.CUSTOM:
        where.push({
          contact: {
            assignee: {
              id: { _in: filters.assignees },
            },
          },
        });
        break;
    }

    if (filters.channelsSlugs.length) {
      where.push({
        lastContactInteraction: {
          channel: {
            _or: filters.channelsSlugs.map((slug) => ({
              slug: { _eq: slug as ContactInteractionChannelSlug },
            })),
          },
        },
      });
    }

    switch (filters.conversationStatus) {
      case 'IS_MARKED':
        where.push({
          isMarked: { _eq: true },
        });
        break;
      case 'IS_SNOOZED':
        where.push({
          isSnoozed: { _eq: true },
        });
        break;
      case 'IS_REPLY_PENDING':
        where.push({
          isReplyPending: { _eq: true },
          isOpen: { _eq: true },
          isSnoozed: { _eq: false },
        });
        break;
      case 'IS_CLOSED':
        where.push({
          isOpen: { _eq: false },
          isSnoozed: { _eq: false },
        });
        break;
      case 'IS_OPEN':
        where.push({
          isOpen: { _eq: true },
          isSnoozed: { _eq: false },
        });
        break;
    }

    if (filters.departmentIds.length) {
      where.push({ department: { id: { _in: filters.departmentIds } } });
    }

    if (filters.contactCampaignsIds.length) {
      where.push({
        contact: { campaign: { id: { _in: filters.contactCampaignsIds } } },
      });
    }

    return {
      institutionId,
      where: { _and: where },
      first: CONVERSATIONS_QUERY_PAGE_SIZE,
      orderBy: { relevantUpdateAt: 'DESC' },
    };
  }, [
    filters.keyword,
    filters.contactTagsIds,
    filters.contactStatusesIds,
    filters.contactStagesIds,
    filters.assigneeType,
    filters.channelsSlugs,
    filters.conversationStatus,
    filters.departmentIds,
    filters.contactCampaignsIds,
    filters.assignees,
    institutionId,
    userId,
  ]);
};
